// src/pages/Dashboard.jsx
import React, { useContext, useState, useEffect, useRef } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { FaEllipsisV } from 'react-icons/fa';
import VenueTrafficDashboard from '../components/analysis/SpotTrafficDashboard';
import AdvLogDashboard from '../components/analysis/AdvLogDashboard';
import ContentLogDashboard from '../components/analysis/ContentLogDashboard';
import RecentChangesDashboard from '../components/analysis/RecentChangesDashboard';
import AppContext from '../contexts/AppContext';
import NAV_CONFIG from '../components/commons/Navconfig';

const Dashboard = () => {
  const { selectedRole } = useContext(AppContext);
  // console.log('Selected Role:', selectedRole);
  const navigate = useNavigate();
  // 这里增加一个强制判断 如果是ts的部分 那就导航到他们自己的页面上去
  const isVenue = selectedRole?.type === 'venue';
  const [menuOpen, setMenuOpen] = useState({
    home: false,
    services: false,
    account: false,
  });

  // refs for detecting outside clicks
  const homeRef = useRef(null);
  const servicesRef = useRef(null);
  const accountRef = useRef(null);

  // toggle menus
  const toggleMenu = (key) => {
    setMenuOpen(prev => ({ ...prev, [key]: !prev[key] }));
  };
  const closeAll = () => setMenuOpen({ home: false, services: false, account: false });

  // click outside to close
  useEffect(() => {
    const handler = (e) => {
      if (
        homeRef.current?.contains(e.target) ||
        servicesRef.current?.contains(e.target) ||
        accountRef.current?.contains(e.target)
      ) return;
      closeAll();
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  // grab nav config items
  const venueNav = NAV_CONFIG.venue;
  const homeItem = venueNav.find(i => i.title === 'Home');
  const servicesItem = venueNav.find(i => i.title === 'Services');
  const accountItem = venueNav.find(i => i.title === 'Account');

  if (selectedRole?.type === 'tsVenue') {
    return <Navigate to="/ts/venue/branding" replace />;
  }
  if (selectedRole?.type === 'tsAdv') {
    return <Navigate to="/ts/adv/media" replace />;
  }
  if (selectedRole?.type === 'tsContent' && selectedRole?.id) {
    // 直接编辑权限的账号只被授权编辑这一个内容节点，没有 tsVenue 角色，
    // 所以不能走 venue 内容树浏览器，直接跳到该节点的编辑页。
    return <Navigate to={`/ts/detail/${selectedRole.id}`} replace />;
  }

  return (
    <div>
      <div className="max-w-7xl w-[90%] mx-auto">
        <h1 className="text-2xl text-blue-600 font-semibold md:block py-4">
          Dashboard
        </h1>

        {isVenue && (
          <div className="p-4 bg-white shadow-md rounded-lg">
            <p className="text-gray-600 mt-4 text-justify md:text-center">
              Welcome to Digital Compendium's content management system, where you can manage your venue.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mt-8 max-w-[90%] mx-auto">

              {/* Home Card */}
              <div className="bg-purple-500 text-white rounded-md p-4 relative min-h-[15vh]">
                <h2 className="text-lg font-semibold">Home</h2>
                <p className="mt-1 text-sm">Manage your home layout & quick links.</p>

                <div
                  className="absolute top-2 right-2 cursor-pointer"
                  onClick={() => toggleMenu('home')}
                  ref={homeRef}
                >
                  <FaEllipsisV size={20} />
                </div>
                {menuOpen.home && (
                  <div className="absolute top-8 right-2 bg-white text-gray-700 shadow-md rounded-lg p-2 z-30">
                    <ul>
                      {homeItem.children.map(child => (
                        <li
                          key={child.title}
                          className="p-2 hover:bg-gray-200 cursor-pointer"
                          onClick={() => {
                            navigate(child.path);
                            closeAll();
                          }}
                        >
                          {child.title}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Services Card */}
              <div className="bg-blue-500 text-white rounded-md p-4 relative min-h-[15vh]">
                <h2 className="text-lg font-semibold">Services</h2>
                <p className="mt-1 text-sm">Manage your services layout & content.</p>

                <div
                  className="absolute top-2 right-2 cursor-pointer"
                  onClick={() => toggleMenu('services')}
                  ref={servicesRef}
                >
                  <FaEllipsisV size={20} />
                </div>
                {menuOpen.services && (
                  <div className="absolute top-8 right-2 bg-white text-gray-700 shadow-md rounded-lg p-2 z-30">
                    <ul>
                      {servicesItem.children.map(child => (
                        <li
                          key={child.title}
                          className="p-2 hover:bg-gray-200 cursor-pointer"
                          onClick={() => {
                            navigate(child.path);
                            closeAll();
                          }}
                        >
                          {child.title}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Account Card */}
              <div className="bg-orange-500 text-white rounded-md p-4 relative min-h-[15vh]">
                <h2 className="text-lg font-semibold">Account</h2>
                <p className="mt-1 text-sm">Manage account settings & users.</p>

                <div
                  className="absolute top-2 right-2 cursor-pointer"
                  onClick={() => toggleMenu('account')}
                  ref={accountRef}
                >
                  <FaEllipsisV size={20} />
                </div>
                {menuOpen.account && (
                  <div className="absolute top-8 right-2 bg-white text-gray-700 shadow-md rounded-lg p-2 z-30">
                    <ul>
                      {accountItem.children.map(child => (
                        <li
                          key={child.title}
                          className="p-2 hover:bg-gray-200 cursor-pointer"
                          onClick={() => {
                            navigate(child.path);
                            closeAll();
                          }}
                        >
                          {child.title}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Analytics Card */}
              <div className="bg-red-500 text-white rounded-md p-4 relative min-h-[15vh]">
                <h2 className="text-lg font-semibold">Analytics</h2>
                <p className="mt-1 text-sm">View performance analytics.</p>
                <button
                  className="absolute bottom-3 right-3 bg-white text-red-500 px-3 py-1 rounded-lg hover:bg-gray-100 text-sm"
                  onClick={() => navigate('/analytics')}
                >
                  View Details
                </button>
              </div>
            </div>

            {/* Dashboards */}
            <div className="p-4 bg-white rounded-lg max-w-[95%] mx-auto mt-8 overflow-y-auto">
              <VenueTrafficDashboard />
            </div>
            <div className="p-4 bg-white rounded-lg max-w-[95%] mx-auto mt-8">
              <ContentLogDashboard />
            </div>
            <div className="p-4 bg-white rounded-lg max-w-[95%] mx-auto mt-8">
              <RecentChangesDashboard />
            </div>
          </div>
        )}

        {selectedRole?.type === 'adv' && (
          <div className="p-4 bg-white shadow-md rounded-lg">
            <p className="text-gray-600 mt-4 text-justify md:text-center">
              Welcome to Digital Compendium's advertiser dashboard.
            </p>
            <div className="p-4 bg-white rounded-lg max-w-[95%] mx-auto mt-5 overflow-y-auto">
              <AdvLogDashboard />
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default Dashboard;
